/**
 * DailyTimelineContainer — smart wrapper that binds the presentational
 * `<DailyTimeline />` surface to the appointments data layer.
 *
 * Responsibilities:
 *   - Day navigation state (anchored to today on mount).
 *   - Data fetching via React Query (`useWeekAppointments`) — keeps a single
 *     cache shared with `<WeeklyCalendarContainer />`, so optimistic status
 *     changes made here show up instantly on the weekly view and vice versa.
 *   - Hydration: ISO → Date conversion (mirrors `hydrateAppointment` from the
 *     weekly calendar container).
 *   - Optimistic status transitions via `useEditAppointment` — sets local cache
 *     before the mutation resolves; rollback on failure.
 *   - Edit flow: opens `<AppointmentForm mode="edit" />` inside the existing
 *     Radix `<SideSheet />` (tablet landscape fallback). The timeline's own
 *     `ResponsiveSheet` is reserved for details / swipe confirmations which
 *     need phone-portrait bottom-sheet behaviour.
 *   - Offline detection via `navigator.onLine`.
 *   - Large mode persistence via localStorage.
 *   - `now` tick every 60 seconds so countdowns / "ŞİMDİ" rail stay fresh.
 *
 * @see PETOTA_BUSINESS_UX_BLUEPRINT §B1.5
 */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { SideSheet } from '@/shared/ui/SideSheet';
import { getMondayOfWeek } from '@/shared/ui/calendar';
import type { Appointment } from '@/shared/ui/calendar/types';
import { DailyTimeline, useTimelineLabels } from '@/shared/ui/timeline';
import type {
  DailyTimelineProps,
  TransitionStatus,
} from '@/shared/ui/timeline';
import { useConnectStatus } from '@/features/connect';

import { AppointmentForm } from './AppointmentForm';
import type { AppointmentFormValues } from './appointment-form-schema';
import { toDateInputValue, toTimeInputValue } from './form-utils';
import type { AppointmentRecord } from './types';
import {
  useCustomers,
  useEditAppointment,
  useServices,
  useWeekAppointments,
} from './useAppointments';

const LARGE_MODE_KEY = 'petota-business:timeline-large-mode';
const NOW_TICK_MS = 60_000;

function hydrateAppointment(record: AppointmentRecord): Appointment {
  return {
    id: record.id,
    source: record.source,
    externalBookingId: record.externalBookingId,
    customerId: record.customerId,
    customerName: record.customerName,
    petId: record.petId,
    petName: record.petName,
    serviceName: record.serviceName,
    serviceCategory: record.serviceCategory,
    startTime: new Date(record.startTime),
    endTime: new Date(record.endTime),
    staffId: record.staffId,
    status: record.status,
    isVip: record.isVip,
    notes: record.notes,
    price: record.price,
    currency: record.currency,
  };
}

function isPetServisAuthorityLocked(appointment: Pick<Appointment, 'source' | 'externalBookingId'>): boolean {
  return appointment.source === 'marketplace' && Boolean(appointment.externalBookingId);
}

function readLargeModeFromStorage(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(LARGE_MODE_KEY) === '1';
  } catch {
    return false;
  }
}

function writeLargeModeToStorage(value: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(LARGE_MODE_KEY, value ? '1' : '0');
  } catch {
    /* non-blocking: storage quota / privacy mode */
  }
}

export function DailyTimelineContainer(): JSX.Element {
  const { t: tForm } = useTranslation('appointmentForm');
  const { t: tTimeline } = useTranslation('timeline');
  const labels = useTimelineLabels();

  const [date, setDate] = useState<Date>(() => new Date());
  const [now, setNow] = useState<Date>(() => new Date());
  const [isLargeMode, setIsLargeMode] = useState<boolean>(readLargeModeFromStorage);
  const [isOffline, setIsOffline] = useState<boolean>(
    () => typeof navigator !== 'undefined' && !navigator.onLine,
  );

  const [editFor, setEditFor] = useState<Appointment | null>(null);

  // Tick `now` every minute so countdown labels and the ŞİMDİ rail stay fresh
  // without re-rendering on every animation frame.
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), NOW_TICK_MS);
    return () => window.clearInterval(id);
  }, []);

  // Offline detection — optimistic commits keep working, banner informs the
  // field worker that changes will sync later.
  useEffect(() => {
    const onOnline = () => setIsOffline(false);
    const onOffline = () => setIsOffline(true);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  // Anchor data fetch to the Monday of the currently viewed day — shared
  // cache key with the WeeklyCalendarContainer, so optimistic updates here
  // appear on that surface too without a refetch.
  const weekStart = useMemo(() => getMondayOfWeek(date), [date]);
  const isoMonday = weekStart.toISOString();

  const appointmentsQuery = useWeekAppointments(isoMonday);
  const connectStatusQuery = useConnectStatus();
  const servicesQuery = useServices();
  const customersQuery = useCustomers();
  const editMutation = useEditAppointment(isoMonday);
  const isPetServisAuthority = connectStatusQuery.data?.syncAuthority === 'petservis';

  const appointments = useMemo(
    () => (appointmentsQuery.data ?? []).map((record) => {
      const appointment = hydrateAppointment(record);
      return {
        ...appointment,
        syncAuthorityLocked: isPetServisAuthority
          && isPetServisAuthorityLocked(appointment),
      };
    }),
    [appointmentsQuery.data, isPetServisAuthority],
  );

  const customers = customersQuery.data ?? [];
  const services = servicesQuery.data ?? [];

  const handleStatusChange: NonNullable<DailyTimelineProps['onStatusChange']> = async (
    id,
    nextStatus,
    reason,
  ) => {
    const appointment = appointments.find((item) => item.id === id);
    if (appointment?.syncAuthorityLocked) {
      toast.error(tTimeline('toasts.authorityLocked'));
      throw new Error('sync-authority-locked');
    }

    const changes: { status: TransitionStatus; notes?: string } = { status: nextStatus };
    if (reason && reason.trim().length > 0) {
      const prevNotes = appointments.find((a) => a.id === id)?.notes ?? '';
      const stamp = `[${new Date().toISOString()}] ${nextStatus}: ${reason.trim()}`;
      changes.notes = prevNotes ? `${prevNotes}\n${stamp}` : stamp;
    }

    try {
      await editMutation.mutateAsync({ id, changes });
      toast.success(tTimeline('toasts.statusChangeSuccess'));
    } catch (err) {
      toast.error(tTimeline('toasts.statusChangeError'));
      throw err instanceof Error ? err : new Error('status-change-failed');
    }
  };

  const handleCallCustomer = (fallbackPhone: string) => {
    // The detail sheet passes `""` because the inline card doesn't know the
    // phone. Look it up from the customers cache using the currently open
    // appointment, if available.
    const appointmentId = editFor?.id;
    let phone = fallbackPhone;
    if (!phone && appointmentId) {
      const appt = appointments.find((a) => a.id === appointmentId);
      if (appt) {
        phone = customers.find((c) => c.id === appt.customerId)?.phone ?? '';
      }
    }
    if (!phone) return;
    if (typeof window !== 'undefined') {
      window.location.href = `tel:${phone.replace(/\s+/g, '')}`;
    }
  };

  const editInitialValues = useMemo<
    (Partial<AppointmentFormValues> & { id?: string }) | undefined
  >(() => {
    if (!editFor) return undefined;
    const service = services.find((s) => s.name === editFor.serviceName);
    return {
      id: editFor.id,
      customerId: editFor.customerId,
      petId: editFor.petId,
      serviceId: service?.id ?? '',
      staffId: editFor.staffId,
      date: toDateInputValue(editFor.startTime),
      startTime: toTimeInputValue(editFor.startTime),
      endTime: toTimeInputValue(editFor.endTime),
      price: editFor.price ?? 0,
      currency: editFor.currency ?? 'TRY',
      notes: editFor.notes ?? '',
      isVip: editFor.isVip ?? false,
    };
  }, [editFor, services]);

  return (
    <>
      <DailyTimeline
        date={date}
        now={now}
        appointments={appointments}
        labels={labels}
        isLoading={appointmentsQuery.isLoading}
        error={
          appointmentsQuery.error
            ? (appointmentsQuery.error as Error).message
            : undefined
        }
        isOffline={isOffline}
        isLargeMode={isLargeMode}
        onLargeModeToggle={(next) => {
          setIsLargeMode(next);
          writeLargeModeToStorage(next);
        }}
        onDateChange={(newDate) => setDate(newDate)}
        onStatusChange={handleStatusChange}
        onEdit={(appointment) => {
          if (appointment.syncAuthorityLocked) {
            toast.error(tTimeline('toasts.authorityLocked'));
            return;
          }
          setEditFor(appointment);
        }}
        onCallCustomer={handleCallCustomer}
      />

      <SideSheet
        open={Boolean(editFor)}
        onOpenChange={(open) => {
          if (!open) setEditFor(null);
        }}
        title={tForm('titleEdit')}
        closeLabel={labels.closeSheet}
      >
        {editFor && (
          <AppointmentForm
            isoMonday={isoMonday}
            mode="edit"
            initialValues={editInitialValues}
            onSuccess={() => setEditFor(null)}
            onCancel={() => setEditFor(null)}
          />
        )}
      </SideSheet>
    </>
  );
}
